import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater } from "use-immer";

import { IgFilterAutocomplete } from "@intelligems/ig-theme";
import {
  AudienceFilter,
  AudienceFilterType,
  ChannelType,
  CountryEnum,
  DeviceType,
  ShopifyCountryList,
  StatSigViewType,
  SubscriptionAnalyticsType,
  UrlParamPayloadType,
  VisitorType,
} from "@intelligems/ig-types";

import { FilterDialogButtons } from "../../../../api/types/dashboards";
import { Channel } from "../../../../components/AudienceFilters/Channel";
import { BaseFiltersDialog } from "../../../../components/DashboardFilters/BaseFiltersDialog";
import { DeviceTypeFilter } from "../../../../components/DashboardFilters/DeviceTypeFilter";
import { SubscriptionTypeFilter } from "../../../../components/DashboardFilters/SubscriptionTypeFilter";
import { UrlParamFilter } from "../../../../components/DashboardFilters/UrlParamFilter";
import { VisitorTypeFilter } from "../../../../components/DashboardFilters/VisitorTypeFilter";
import { setDialog } from "../../../../store/slices/global";
import { setSitewideDashboardFilter } from "../../../../store/slices/sitewideDashboardFilters";
import { SitewideDashboardFiltersDialog } from "../../../../store/types/sitewideDashboardFilters";
import { IAppState } from "../../../../store/types/store";

interface SitewideFiltersDialogProps {
  openFilters: boolean;
  dialogFilters: SitewideDashboardFiltersDialog;
  setDialogFilters: Updater<SitewideDashboardFiltersDialog>;
}

export const SitewideFiltersDialog = ({
  openFilters,
  dialogFilters,
  setDialogFilters,
}: SitewideFiltersDialogProps) => {
  const dispatch = useDispatch();
  const sitewideDashboardFilters = useSelector(
    (state: IAppState) => state.sitewideDashboardFilters
  );

  const onStringFilterChange = (
    newValue:
      | DeviceType
      | VisitorType
      | ChannelType
      | StatSigViewType
      | SubscriptionAnalyticsType,
    filterKey:
      | "statSigView"
      | "deviceType"
      | "channelType"
      | "visitorType"
      | "subscriptionType"
  ) => {
    setDialogFilters((draft) => {
      if (filterKey === "statSigView") {
        draft[filterKey] = newValue as StatSigViewType;
      } else if (filterKey === "deviceType") {
        draft[filterKey] = newValue as DeviceType;
      } else if (filterKey === "channelType") {
        draft[filterKey] = newValue as ChannelType;
      } else if (filterKey === "visitorType") {
        draft[filterKey] = newValue as VisitorType;
      } else if (filterKey === "subscriptionType") {
        draft[filterKey] = newValue as SubscriptionAnalyticsType;
      }
      return draft;
    });
  };
  const onStringArrFilterChange = (
    newValues: string[],
    filterKey: "customEvents" | "countryCodes"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValues;
      return draft;
    });
  };
  const onChannelClick = (channelType: ChannelType | undefined) => {
    const updatedChannels = dialogFilters.channels?.filter(
      (channel) => !!channel
    );
    if (!channelType) {
      setDialogFilters((draft) => {
        draft.channels = [];
        return draft;
      });
    } else if (updatedChannels?.includes(channelType)) {
      setDialogFilters((draft) => {
        if (draft.channels?.length === 1) {
          // always default back to any when nothing else selected
          draft.channels = [];
        } else {
          draft.channels = draft.channels?.filter(
            (channel) => channel !== channelType
          );
        }
        return draft;
      });
    } else {
      setDialogFilters((draft) => {
        draft.channels = [...(updatedChannels || []), channelType];
      });
    }
  };
  const onUrlParamChange = (
    value: AudienceFilterType | string | null,
    key: keyof UrlParamPayloadType
  ) => {
    setDialogFilters((draft) => {
      if (!draft.urlParam) {
        draft.urlParam = {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        };
        if (key === "queryParamFilterType") {
          draft.urlParam.queryParamFilterType = value as AudienceFilter;
        } else {
          draft.urlParam[key] = value;
        }
      } else {
        if (key === "queryParamFilterType") {
          draft.urlParam.queryParamFilterType = value as AudienceFilter;
        } else {
          draft.urlParam[key] = value;
        }
      }
      return draft;
    });
  };
  const filterButtons: FilterDialogButtons[] = [
    {
      label: "Device Type",
      settings: [
        {
          key: "deviceType",
          description: "Filter by device type",
          component: (
            <DeviceTypeFilter
              onStringFilterChange={onStringFilterChange}
              deviceType={dialogFilters.deviceType}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Visitor Type",
      settings: [
        {
          key: "visitorType",
          description: "Filter by visitor type",
          component: (
            <VisitorTypeFilter
              onStringFilterChange={onStringFilterChange}
              visitorType={dialogFilters.visitorType}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Subscription Type",
      settings: [
        {
          key: "subscriptionType",
          description: "Filter by subscription type",
          component: (
            <SubscriptionTypeFilter
              onStringFilterChange={onStringFilterChange}
              subscriptionType={dialogFilters.subscriptionType}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Traffic Sources",
      settings: [
        {
          key: "channelType",
          description: "Filter by traffic source type",
          component: (
            <Channel
              onClick={onChannelClick}
              channels={dialogFilters.channels}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "URL Parameter",
      settings: [
        {
          key: "urlParam",
          description: "Filter to visitors from specific ad campaigns or media",
          component: (
            <UrlParamFilter
              onUrlParamChange={onUrlParamChange}
              urlParam={dialogFilters.urlParam}
              onReset={() => {
                setDialogFilters((draft) => {
                  draft.urlParam = {
                    queryParam: null,
                    queryParamValue: null,
                    queryParamFilterType: null,
                  };
                  return draft;
                });
              }}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Country Codes",
      settings: [
        {
          key: "countryCodes",
          description: "Filter to visitors who were in one of these countries",
          component: (
            <IgFilterAutocomplete
              selectedValues={dialogFilters.countryCodes}
              onChange={onStringArrFilterChange}
              filterKey={"countryCodes"}
              allDataOptions={[...ShopifyCountryList]}
              placeholder={"Countries"}
              disableCloseOnSelect={true}
              getOptionLabel={(option) => `${CountryEnum[option]} (${option})`}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
  ];

  const onFiltersClearAll = () => {
    dispatch(
      setSitewideDashboardFilter({
        ...sitewideDashboardFilters,
        deviceType: null,
        visitorType: null,
        countryCodes: [],
        subscriptionType: null,
        channels: [],
        urlParam: {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        },
      })
    );

    setDialogFilters((draft) => {
      draft.deviceType = null;
      draft.visitorType = null;
      draft.countryCodes = [];
      draft.subscriptionType = null;
      draft.urlParam = {
        queryParam: null,
        queryParamFilterType: null,
        queryParamValue: null,
      };
      draft.channels = [];
      return draft;
    });

    dispatch(setDialog({ dialog: "dashboardFilters", open: false }));
  };

  const onFiltersSave = () => {
    dispatch(
      setSitewideDashboardFilter({
        ...sitewideDashboardFilters,
        deviceType: dialogFilters.deviceType,
        visitorType: dialogFilters.visitorType,
        countryCodes: dialogFilters.countryCodes,
        subscriptionType: dialogFilters.subscriptionType,
        channels: dialogFilters.channels,
        urlParam: dialogFilters.urlParam,
      })
    );
    dispatch(setDialog({ dialog: "dashboardFilters", open: false }));
  };

  return (
    <BaseFiltersDialog
      onFilterSave={onFiltersSave}
      openFilters={openFilters}
      onFiltersClearAll={onFiltersClearAll}
      filterButtons={filterButtons}
    />
  );
};
